import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Camera } from "lucide-react";
import { PROFILE } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

const LINES = [
  <>Creative that <em className="italic font-medium text-copper-600">communicates.</em></>,
  <>Operations that <em className="italic font-medium text-copper-600">deliver.</em></>,
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (e, href) => {
    e.preventDefault();
    scrollToSection(href);
  };

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-center gap-3 mb-10"
            >
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-charcoal-500">Executive Profile</span>
              <span className="h-px w-16 bg-charcoal-900/20" />
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="font-serif tracking-tight leading-[1.08] text-charcoal-900 text-[clamp(2.5rem,5.6vw,4.75rem)]"
            >
              {LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.95, delay: 0.2 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9"
            >
              <p className="font-mono text-xs tracking-[0.22em] uppercase text-copper-600 mb-3" data-testid="hero-name">
                {PROFILE.name}
              </p>
              <p className="text-lg sm:text-xl font-semibold text-charcoal-900" data-testid="hero-title">
                {PROFILE.title}
              </p>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-charcoal-600 max-w-xl" data-testid="hero-subtext">
                14+ years of experience leading creative teams, managing content and production
                workflows, and translating business requirements into high-quality,
                ready-to-publish communication.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  data-testid="hero-view-work-button"
                  onClick={(e) => go(e, "#portfolio")}
                  className="group inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-7 py-3.5 hover:bg-copper-600 transition-colors duration-300"
                >
                  View My Work
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <button
                  data-testid="hero-connect-button"
                  onClick={(e) => go(e, "#contact")}
                  className="inline-flex items-center gap-2 rounded-full border border-charcoal-900/25 text-charcoal-900 text-sm font-medium px-7 py-3.5 hover:border-copper-600 hover:text-copper-600 transition-colors duration-300"
                >
                  Let's Connect
                </button>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 28, rotate: 1.5 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 flex justify-center lg:justify-end"
          >
            <div data-testid="hero-portrait-frame" className="group relative">
              <span
                aria-hidden="true"
                className="absolute -inset-3 rounded-[2rem] border border-copper-600/30 rotate-2 transition-transform duration-500 group-hover:rotate-3"
              />
              <div className="relative w-72 sm:w-80 lg:w-[21rem] aspect-[4/5] rounded-[1.75rem] bg-surface-white border border-charcoal-900/10 shadow-2xl overflow-hidden transition-transform duration-500 group-hover:-rotate-1">
                {PROFILE.photo ? (
                  <img
                    src={PROFILE.photo}
                    alt={`Portrait of ${PROFILE.name}`}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div
                    className="w-full h-full flex flex-col items-center justify-center gap-4"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(135deg, rgba(18,19,22,0.03) 0 1px, transparent 1px 14px)",
                    }}
                  >
                    <span className="font-serif italic text-8xl text-charcoal-900/10 select-none">SP</span>
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-charcoal-500 border border-charcoal-900/15 rounded-full px-4 py-1.5 bg-parchment/70">
                      <Camera className="w-3.5 h-3.5" /> Your photo here
                    </span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.button
          data-testid="hero-scroll-indicator"
          onClick={(e) => go(e, "#about")}
          style={{ opacity: fade }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute -bottom-2 left-4 sm:left-6 lg:left-8 flex items-center gap-3 text-charcoal-500 hover:text-copper-600 transition-colors"
          aria-label="Scroll to about section"
        >
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <ArrowDown className="w-4 h-4" />
          </motion.span>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Scroll</span>
        </motion.button>
      </div>
    </section>
  );
}
